import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import authReducer from '@/features/auth/slices/authSlice';
import legalReducer from '@/features/legal/slices/legalSlice';
import serviceTypesReducer from '@/features/service-types/slices/service-typesSlice';
import workspacesReducer from '@/features/workspaces/slices/workspacesSlice';

import { baseApi } from './baseApi';

const appReducer = combineReducers({
  [baseApi.reducerPath]: baseApi.reducer,
  auth: authReducer,
  legal: legalReducer,
  workspaces: workspacesReducer,
  serviceTypes: serviceTypesReducer,
});

const rootReducer = (state: any, action: any) => {
  if (action.type === 'auth/clearAuth') {
    state = undefined; // * Resets the whole store.
  }
  return appReducer(state, action);
};

const persistConfig = {
  key: 'netnet-root',
  storage,
  whitelist: ['auth', 'workspaces'], // * Only these will be persisted.
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const makeStore = () =>
  configureStore({
    reducer: persistedReducer,
    middleware: (getDefault) =>
      getDefault({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat(baseApi.middleware),
    devTools: process.env.NODE_ENV !== 'production',
  });

export const store = makeStore();
setupListeners(store.dispatch);
export const persistor = persistStore(store);

export type AppStore = ReturnType<typeof makeStore>;
export type AppDispatch = AppStore['dispatch'];
export type RootState = ReturnType<AppStore['getState']>;
